import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react'
import api, { handleApiError } from '../services/api'

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .email('כתובת אימייל לא תקינה')
    .min(1, 'שדה חובה'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      await api.post('/auth/forgot-password', { email: data.email })
      setSubmittedEmail(data.email)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (submittedEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle size={32} className="text-green-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              בדוק/י את תיבת הדואר שלך
            </h2>
            <p className="text-gray-600 mb-2">
              אם הכתובת <span className="font-semibold" dir="ltr">{submittedEmail}</span> רשומה במערכת,
              שלחנו אליה קישור לאיפוס הסיסמה.
            </p>
            <p className="text-gray-600 mb-6">
              אנא בדוק/י גם את תיקיית הספאם. הקישור תקף לשעה אחת.
            </p>
            <Link to="/login" className="btn btn-outline inline-flex items-center gap-2">
              <ArrowRight size={18} aria-hidden="true" />
              <span>חזרה להתחברות</span>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold text-primary-600 mb-2">
            יפתח ונגר יועץ השקעות
          </h1>
          <p className="text-gray-600">איפוס סיסמה</p>
        </div>

        <div className="card">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            שכחת את הסיסמה?
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            הזן/י את כתובת הדוא"ל שלך ואנחנו נשלח אליך קישור לאיפוס הסיסמה.
          </p>

          {error && (
            <div className="alert alert-error mb-6" role="alert">
              <AlertCircle size={20} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                כתובת אימייל <span className="text-red-500">*</span>
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder="example@email.com"
                autoComplete="email"
                dir="ltr"
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p id="email-error" role="alert" className="text-sm text-red-600 mt-1">{errors.email.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner" />
                  <span>שולח...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Mail size={20} aria-hidden="true" />
                  <span>שלח קישור לאיפוס</span>
                </div>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              חזרה להתחברות
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
